import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, SelectLabel, OptionalSection, Section, Message, TooltipHelp, TranslateDropdown } from '@weezevent/nacre';
import EditorComponent from '../../nacre/components/editorComponent';

import Styles from '../../styles/eventCreation/eventCreation.module.css';
import SelectTaxes from '../../components/tax/selectTaxes';
import UploadFileZone from '../../components/upload/uploadFileZone';
import { useSegment } from '../../components/tracking/trackingProvider';
import { removeEmoji } from './utils';

export default function EventCreationInfos({
    onChange,
    organization,
    onDone = null,
    formEvent,
    subtypesOptions,
    onChangeObject,
    isSubmitting,
    children,
    onCardDone = () => {},
    edit = false,
    taxes = null,
    defaultLanguage,
    languageOptions,
    handleTranslations = () => {},
    initLanguage = () => {}
}) {
    const { t } = useTranslation();
    const { track } = useSegment();

    const _onDone = React.useCallback(() => {
        onDone(onCardDone);
    }, [onCardDone, onDone]);

    const [selectedDescriptionLanguage, setSelectedDescriptionLanguage] = React.useState(defaultLanguage);

    const hasTranslations = React.useCallback(property => formEvent.translations && Object.keys(formEvent.translations) && formEvent.translations[property]);

    const eventDescriptionValue = React.useMemo(() => {
        if (!hasTranslations('description') || selectedDescriptionLanguage === defaultLanguage) {
            return formEvent.description;
        }
        return formEvent.translations['description'][selectedDescriptionLanguage] || '';
    }, [defaultLanguage, formEvent, selectedDescriptionLanguage, hasTranslations]);

    const filledDescriptionKeys = React.useMemo(() => {
        const filledDescriptions = [];
        if (formEvent.description?.length) {
            filledDescriptions.push(defaultLanguage);
        }
        if (!hasTranslations('description')) {
            return filledDescriptions;
        }
        const allowedLanguages = languageOptions.map(l => l.value).filter(l => l !== defaultLanguage);
        let keysSelected = Object.keys(formEvent.translations['description']).filter(k => formEvent.translations['description'][k] !== '' && allowedLanguages.includes(k));
        return filledDescriptions.concat(keysSelected);
    }, [defaultLanguage, formEvent, languageOptions, hasTranslations]);

    const [displayDescription, setDisplayDescription] = React.useState(formEvent?.description.length > 0 || filledDescriptionKeys?.length > 0);

    return (
        <>
            <Form.Field style={{ marginBottom: '0' }}>
                <Form.Label label={t('ticket.creationEvent.infos.formLabelName')} />
                <Form.Input
                    showSizes={true}
                    maxLength={80}
                    name="name"
                    placeholder={t('ticket.creationEvent.infos.formPlaceholderName')}
                    value={formEvent?.name}
                    onChange={({ target: { value } }) => {
                        onChange('name', removeEmoji(value));
                    }}
                    autoFocus={!formEvent?.name || formEvent?.name === ''}
                />
            </Form.Field>

            {edit && (
                <OptionalSection
                    className={Styles['event-creation-infos__optional-section']}
                    label={t('ticket.creationEvent.infos.formLabelSubtitle')}
                    open={formEvent?.surtitle.length > 0 || formEvent?.subtitle.length > 0}
                    optionalText={t('ticket.common.labelOptional')}
                >
                    <Section className={Styles['section_title']}>
                        <Form.Field>
                            <Form.Label label={t('ticket.event.uptitle')} />
                            <Form.Input
                                onChange={event => onChange('surtitle', removeEmoji(event.target.value))}
                                placeholder={t('ticket.event.uptitlePlaceholder')}
                                value={formEvent.surtitle}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Form.Label label={t('ticket.event.subtitle')} />
                            <Form.Input
                                onChange={event => onChange('subtitle', removeEmoji(event.target.value))}
                                placeholder={t('ticket.event.subtitlePlaceholder')}
                                value={formEvent.subtitle}
                            />
                        </Form.Field>
                    </Section>
                </OptionalSection>
            )}

            <Form.Field style={{ marginBottom: '20px', marginTop: '0' }}>
                <SelectLabel
                    value={formEvent?.subtype}
                    label={t('ticket.creationEvent.infos.formLabelSubtype')}
                    onChange={value => {
                        onChangeObject({
                            subtype: value
                            // rate: value === 'online_donations' ? rateTypes.open : rateTypes.fixed
                        });
                        let subtypesOption = subtypesOptions?.find(subtypeOption => subtypeOption.value === value);
                        track('ACTION > SELECT EVENT TYPE', {
                            'Event type ID': subtypesOption?.external_id,
                            'Event type name': subtypesOption?.text,
                            'Page Title': 'CREATE EVENT'
                        });
                    }}
                    options={subtypesOptions}
                    placeholder={t('ticket.creationEvent.infos.formPlaceholderSubtype')}
                />
            </Form.Field>

            {edit && (
                <div>
                    <Form.Field>
                        <Form.Label label={t('ticket.event.website')} optionalText={t('ticket.common.labelOptional')} />
                        <Form.Input onChange={event => onChange('website', event.target.value)} placeholder={t('ticket.event.websitePlaceholder')} value={formEvent.website} />
                    </Form.Field>

                    <SelectTaxes
                        label={t('ticket.event.tax')}
                        taxes={taxes}
                        onChange={(optionValue, option) => onChange('tax_id', optionValue)}
                        value={formEvent.tax_id}
                        eventCountry={formEvent?.address?.country}
                    />
                </div>
            )}

            <OptionalSection
                label={t('ticket.creationEvent.infos.formLabelDescription')}
                open={displayDescription}
                optionalText={t('ticket.common.labelOptional')}
                onToggle={open => {
                    setDisplayDescription(open);
                    track('ACTION > DISPLAY DESCRIPTION', {
                        'Page Title': 'CREATE EVENT',
                        Open: open
                    });
                }}
            >
                <Form.Label>
                    <div className={Styles['event-name-input']}>
                        <span>
                            {t('ticket.creationEvent.infos.labelDescription')}
                            <TooltipHelp title={t('ticket.creationEvent.infos.descriptionTooltip')} />
                            <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                        </span>

                        {formEvent && (
                            <TranslateDropdown
                                defaultValue={defaultLanguage}
                                languages={languageOptions}
                                selected={filledDescriptionKeys}
                                onChange={value => {
                                    setSelectedDescriptionLanguage(value);
                                    initLanguage(value, 'description');
                                }}
                            />
                        )}
                    </div>
                </Form.Label>
                <Form.Sublabel label={t('ticket.creationEvent.infos.sublabelDescription')} />

                <EditorComponent
                    description={eventDescriptionValue}
                    onChange={(content, editor) => {
                        handleTranslations(removeEmoji(content), 'description', selectedDescriptionLanguage);
                    }}
                    placeholder={t('ticket.creationEvent.infos.formPlaceholderDescription')}
                    menubar={false}
                />
                {formEvent.is_passculture_activated && formEvent?.description.length > 1000 && (
                    <Message type="warning">{t('ticket.creationEvent.infos.description-warning-message')}</Message>
                )}
            </OptionalSection>

            <div className={Styles['upload-image__container']}>
                <UploadFileZone
                    className={edit ? 'without-margin' : ''}
                    organization={organization}
                    onUpload={image => {
                        onChange('full_picture', image.url);
                        track('ACTION > UPLOAD PICTURE', {
                            'Page Title': 'CREATE EVENT',
                            Upload: true
                        });
                    }}
                    onError={e => console.log('error => ', e)}
                    title={t('ticket.event.picture')}
                    subtitle={t('ticket.creationEvent.infos.formSubLabelEventVisual')}
                    labelDropzone={t('ticket.event.dropzone_label')}
                    subLabelDropzone={t('ticket.event.dropzone_label_min_size')}
                    maxSize={2097152}
                    file={formEvent.full_picture}
                    onDelete={() => onChange('full_picture', null)}
                    optional
                    cropModalProps={{
                        ratio: 16 / 9,
                        forceRatio: true
                    }}
                />
            </div>
            <div className={Styles['buttons-submit']}>
                {onDone && (
                    <Button
                        primary
                        className={Styles['button-continue']}
                        label={t('ticket.common.action.continue')}
                        onClick={_onDone}
                        isLoading={isSubmitting}
                        disabled={formEvent?.name?.length === 0 || formEvent?.subtype === null}
                    />
                )}
                {React.Children.map(children, element => {
                    return React.cloneElement(element, { ...element.props, disabled: formEvent?.name?.length === 0 || formEvent?.subtype === null });
                })}
            </div>
        </>
    );
}

EventCreationInfos.propTypes = {
    onChange: PropTypes.func.isRequired,
    organization: PropTypes.object.isRequired,
    onChangeObject: PropTypes.func.isRequired,
    onDone: PropTypes.func,
    onCardDone: PropTypes.func,
    formEvent: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    subtypesOptions: PropTypes.array.isRequired,
    children: PropTypes.node,
    edit: PropTypes.bool,
    taxsOptions: PropTypes.array,
    defaultLanguage: PropTypes.string.isRequired,
    languageOptions: PropTypes.array.isRequired,
    handleLanguage: PropTypes.func,
    handleTranslations: PropTypes.func
};
